import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Sparkles, X as XIcon, ArrowLeft, Check } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { ThemedText, Button, Input } from '../';
import { aiFormSchemaService } from '../../api';
import { renderFormIcon, LUCIDE_ICON_MAP } from '../../utils/iconUtils';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${props => props.theme.spacing.lg};
`;

const ModalContent = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  padding: ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${props => props.theme.colors.backgroundSecondary};
`;

const ModalBody = styled.div`
  padding: ${props => props.theme.spacing.lg};
`;

const ModalFooter = styled.div`
  padding: ${props => props.theme.spacing.lg};
  border-top: 1px solid ${props => props.theme.colors.border};
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.backgroundSecondary};
`;

const FormGroup = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${props => props.theme.spacing.sm};
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  font-family: inherit;
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
  }

  &::placeholder {
    color: ${props => props.theme.colors.muted};
  }
`;

const CharCount = styled.span`
  display: block;
  text-align: right;
  margin-top: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
`;

const Toggle = styled.input.attrs({ type: 'checkbox' })`
  width: 44px;
  height: 24px;
  appearance: none;
  background-color: ${props => props.checked ? props.theme.colors.tint : props.theme.colors.border};
  border-radius: 12px;
  position: relative;
  cursor: pointer;
  transition: background-color 0.2s;

  &::before {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: white;
    top: 2px;
    left: ${props => props.checked ? '22px' : '2px'};
    transition: left 0.2s;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.xl};
  gap: ${props => props.theme.spacing.md};
`;

const Spinner = styled.div`
  border: 4px solid ${props => props.theme.colors.border};
  border-top: 4px solid ${props => props.theme.colors.tint};
  border-radius: 50%;
  width: 48px;
  height: 48px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ResultsContainer = styled.div`
  margin-top: ${props => props.theme.spacing.lg};
  padding: ${props => props.theme.spacing.lg};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
`;

const MetadataGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MetadataItem = styled.div`
  padding: ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
`;

const MetadataLabel = styled.div`
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const MetadataValue = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const ValidationSection = styled.div`
  margin-top: ${props => props.theme.spacing.md};
`;

const ValidationMessage = styled.div`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  border-left: 3px solid ${props =>
    props.severity === 'Error' ? '#ef4444' :
    props.severity === 'Warning' ? '#f59e0b' : '#3b82f6'};
  background-color: ${props =>
    props.severity === 'Error' ? '#fef2f2' :
    props.severity === 'Warning' ? '#fffbeb' : '#eff6ff'};
  color: ${props =>
    props.severity === 'Error' ? '#991b1b' :
    props.severity === 'Warning' ? '#92400e' : '#1e40af'};
`;

const ReasoningBox = styled.div`
  margin-top: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
  font-size: ${props => props.theme.typography.sizes.small};
  line-height: 1.6;
`;

const InfoBadge = styled.span`
  display: inline-block;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  background-color: ${props => props.theme.colors.tint}20;
  color: ${props => props.theme.colors.tint};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  margin-left: ${props => props.theme.spacing.sm};
`;

const PROMPT_EXAMPLES = [
  { label: 'Site Inspection', prompt: 'Site inspection form with location capture, photos, condition ratings for structure and equipment, and inspector signature' },
  { label: 'Customer Intake', prompt: 'Customer intake form with contact details, service type selection, address, preferred schedule, and notes' },
  { label: 'Equipment Check', prompt: 'Daily equipment check with pass/fail toggles for each item, photo evidence field, maintenance notes, and technician sign-off' },
  { label: 'Water Quality', prompt: 'Water quality test form with sample location, date and time, pH, turbidity, chlorine, and lab notes' },
  { label: 'Incident Report', prompt: 'Incident report with date/time, location, description, severity rating, involved parties, corrective action, and witness info' },
  { label: 'Work Order Completion', prompt: 'Work order completion report with job details, time on site, materials used, before/after photos, and customer sign-off' },
  { label: 'Safety Checklist', prompt: 'Employee safety checklist with toggle fields for each safety item, hazard photo capture, date, location, and supervisor signature' },
  { label: 'Vehicle Inspection', prompt: 'Vehicle pre-trip inspection with checkbox groups for lights, tires, brakes, fluids, and damage photo upload' },
];

export const AIGenerationModal = ({ isOpen, onClose, onApply, currentFields }) => {
  const theme = useTheme();
  const [description, setDescription] = useState('');
  const [improveCurrentForm, setImproveCurrentForm] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationResult, setGenerationResult] = useState(null);
  const [streamingReasoning, setStreamingReasoning] = useState('');
  const [isBuilding, setIsBuilding] = useState(false);
  // Refinement chat state
  const [chatMessage, setChatMessage] = useState('');
  const [isRefining, setIsRefining] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const abortControllerRef = React.useRef(null);

  if (!isOpen) return null;

  const handleGenerate = async () => {
    const validation = aiFormSchemaService.validateRequest({ description });
    if (!validation.isValid) {
      validation.errors.forEach(error => toast.error(error));
      return;
    }

    setIsGenerating(true);
    setGenerationResult(null);
    setStreamingReasoning('');
    setIsBuilding(false);
    setConversationHistory([]);

    const requestData = {
      description: description.trim(),
    };

    if (improveCurrentForm && currentFields && currentFields.length > 0) {
      requestData.requirements = `Improving existing form with ${currentFields.length} fields.`;
    }

    abortControllerRef.current = new AbortController();

    try {
      await aiFormSchemaService.generateFormSchemaStream(
        requestData,
        {
          onReasoning: (text) => setStreamingReasoning(prev => prev + text),
          onBuilding: () => setIsBuilding(true),
          onComplete: (result) => {
            setGenerationResult(result);
            setIsGenerating(false);
            setIsBuilding(false);
            if (result.success) toast.success('Form generated successfully!');
            else toast.error(result.errorMessage || 'Failed to generate form');
          },
          onError: (msg) => {
            toast.error('Generation error: ' + msg);
            setIsGenerating(false);
            setIsBuilding(false);
          },
        },
        abortControllerRef.current.signal
      );
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Failed to generate form:', error);
        toast.error('Failed to generate form: ' + (error.message || 'Unknown error'));
      }
      setIsGenerating(false);
      setIsBuilding(false);
    }
  };

  const handleRefine = async () => {
    if (!chatMessage.trim() || !generationResult?.schemaDefinition) return;

    setIsRefining(true);
    const userMsg = chatMessage.trim();
    setChatMessage('');

    const updatedHistory = [...conversationHistory, { role: 'user', content: userMsg }];
    setConversationHistory(updatedHistory);

    try {
      const result = await aiFormSchemaService.refineSchema({
        currentSchema: generationResult.schemaDefinition,
        message: userMsg,
        conversationHistory: updatedHistory.slice(-6),
      });

      if (result.success) {
        setGenerationResult(result);
        setConversationHistory(prev => [...prev, { role: 'assistant', content: result.reasoning || 'Schema updated.' }]);
        toast.success('Schema refined!');
      } else {
        toast.error(result.errorMessage || 'Failed to refine schema');
        setConversationHistory(prev => prev.slice(0, -1));
      }
    } catch (error) {
      console.error('Failed to refine schema:', error);
      toast.error('Failed to refine: ' + (error.message || 'Unknown error'));
      setConversationHistory(prev => prev.slice(0, -1));
    } finally {
      setIsRefining(false);
    }
  };

  const handleApply = () => {
    if (generationResult && generationResult.success) {
      onApply(generationResult);
      handleClose();
    }
  };

  const handleClose = () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    setDescription('');
    setImproveCurrentForm(false);
    setGenerationResult(null);
    setStreamingReasoning('');
    setIsBuilding(false);
    setChatMessage('');
    setIsRefining(false);
    setConversationHistory([]);
    onClose();
  };

  const hasCurrentFields = currentFields && currentFields.length > 0;

  return (
    <ModalOverlay theme={theme}>
      <ModalContent theme={theme} onClick={(e) => e.stopPropagation()}>
        <ModalHeader theme={theme}>
          <ThemedText type="subtitle"><Sparkles size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />Generate Form with AI</ThemedText>
          <Button variant="text" onClick={handleClose} size="sm"><XIcon size={18} /></Button>
        </ModalHeader>

        <ModalBody theme={theme}>
          {!isGenerating && !generationResult && (
            <>
              <FormGroup theme={theme}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: theme.spacing.sm }}>
                  <Label theme={theme} style={{ margin: 0 }}>
                    Describe your form *
                    <InfoBadge theme={theme}>Required</InfoBadge>
                  </Label>
                </div>
                {/* Pre-made prompt chips */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing.xs, marginBottom: theme.spacing.sm }}>
                  {PROMPT_EXAMPLES.map((ex) => (
                    <button
                      key={ex.label}
                      type="button"
                      onClick={() => setDescription(ex.prompt)}
                      style={{
                        padding: `2px ${theme.spacing.sm}`,
                        fontSize: '11px',
                        fontWeight: 600,
                        background: description === ex.prompt ? `${theme.colors.tint}25` : theme.colors.backgroundSecondary,
                        border: `1px solid ${description === ex.prompt ? theme.colors.tint : theme.colors.border}`,
                        borderRadius: '20px',
                        cursor: 'pointer',
                        color: description === ex.prompt ? theme.colors.tint : theme.colors.muted,
                        transition: 'all 0.15s',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {ex.label}
                    </button>
                  ))}
                </div>
                <TextArea
                  theme={theme}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the form you want to create..."
                  maxLength={2000}
                  style={{ minHeight: '110px' }}
                />
                <CharCount theme={theme}>{description.length} / 2000</CharCount>
              </FormGroup>

              {hasCurrentFields && (
                <FormGroup theme={theme}>
                  <ToggleContainer theme={theme}>
                    <Toggle
                      theme={theme}
                      checked={improveCurrentForm}
                      onChange={(e) => setImproveCurrentForm(e.target.checked)}
                    />
                    <div>
                      <ThemedText type="defaultSemiBold">Improve Current Form</ThemedText>
                      <ThemedText type="small" style={{ color: theme.colors.muted }}>
                        AI will analyze and improve your current {currentFields.length} field(s)
                      </ThemedText>
                    </div>
                  </ToggleContainer>
                </FormGroup>
              )}
            </>
          )}

          {isGenerating && (
            <LoadingContainer theme={theme}>
              <Spinner theme={theme} />
              <ThemedText type="subtitle">
                {isBuilding ? 'Assembling schema...' : 'Thinking...'}
              </ThemedText>
              {streamingReasoning ? (
                <ReasoningBox theme={theme} style={{ maxHeight: '200px', overflowY: 'auto', width: '100%', textAlign: 'left' }}>
                  {streamingReasoning}
                  <span style={{ display: 'inline-block', width: 2, height: '1em', background: theme.colors.tint, marginLeft: 2, animation: 'blink 1s step-end infinite' }} />
                </ReasoningBox>
              ) : (
                <ThemedText style={{ color: theme.colors.muted }}>
                  This may take a few moments
                </ThemedText>
              )}
            </LoadingContainer>
          )}

          {generationResult && (
            <ResultsContainer theme={theme}>
              <ThemedText type="subtitle" style={{ marginBottom: theme.spacing.md }}>
                Generated Form Preview
              </ThemedText>

              {generationResult.success ? (
                <>
                  <MetadataGrid theme={theme}>
                    {generationResult.suggestedName && (
                      <MetadataItem theme={theme}>
                        <MetadataLabel theme={theme}>Suggested Name</MetadataLabel>
                        <MetadataValue theme={theme}>{generationResult.suggestedName}</MetadataValue>
                      </MetadataItem>
                    )}
                    {generationResult.suggestedCategory && (
                      <MetadataItem theme={theme}>
                        <MetadataLabel theme={theme}>Category</MetadataLabel>
                        <MetadataValue theme={theme}>{generationResult.suggestedCategory}</MetadataValue>
                      </MetadataItem>
                    )}
                    {generationResult.schemaDefinition?.icon && (
                      <MetadataItem theme={theme}>
                        <MetadataLabel theme={theme}>Form Icon</MetadataLabel>
                        <MetadataValue theme={theme} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {LUCIDE_ICON_MAP[generationResult.schemaDefinition.icon]
                            ? renderFormIcon(generationResult.schemaDefinition.icon, 24)
                            : <span style={{ fontSize: '13px', color: 'inherit', opacity: 0.6 }}>Icon not compatible — default will be used</span>
                          }
                        </MetadataValue>
                      </MetadataItem>
                    )}
                    {generationResult.tokensUsed && (
                      <MetadataItem theme={theme}>
                        <MetadataLabel theme={theme}>Tokens Used</MetadataLabel>
                        <MetadataValue theme={theme}>{generationResult.tokensUsed}</MetadataValue>
                      </MetadataItem>
                    )}
                    {generationResult.schemaDefinition?.fields && (
                      <MetadataItem theme={theme}>
                        <MetadataLabel theme={theme}>Fields Generated</MetadataLabel>
                        <MetadataValue theme={theme}>
                          {generationResult.schemaDefinition.fields.length} fields
                        </MetadataValue>
                      </MetadataItem>
                    )}
                  </MetadataGrid>

                  {generationResult.suggestedDescription && (
                    <FormGroup theme={theme}>
                      <MetadataLabel theme={theme}>Description</MetadataLabel>
                      <ThemedText>{generationResult.suggestedDescription}</ThemedText>
                    </FormGroup>
                  )}

                  {generationResult.suggestedTags && (
                    <FormGroup theme={theme}>
                      <MetadataLabel theme={theme}>Tags</MetadataLabel>
                      <ThemedText>{generationResult.suggestedTags}</ThemedText>
                    </FormGroup>
                  )}

                  {generationResult.validationResult && (
                    <ValidationSection theme={theme}>
                      {generationResult.validationResult.errors?.length > 0 && (
                        <>
                          <ThemedText type="defaultSemiBold" style={{ marginBottom: theme.spacing.sm }}>
                            Validation Errors
                          </ThemedText>
                          {generationResult.validationResult.errors.map((error, index) => (
                            <ValidationMessage key={index} severity="Error" theme={theme}>
                              {error.fieldKey && <strong>{error.fieldKey}:</strong>} {error.message}
                            </ValidationMessage>
                          ))}
                        </>
                      )}

                      {generationResult.validationResult.warnings?.length > 0 && (
                        <>
                          <ThemedText type="defaultSemiBold" style={{ marginBottom: theme.spacing.sm }}>
                            Warnings
                          </ThemedText>
                          {generationResult.validationResult.warnings.map((warning, index) => (
                            <ValidationMessage key={index} severity="Warning" theme={theme}>
                              {warning.fieldKey && <strong>{warning.fieldKey}:</strong>} {warning.message}
                            </ValidationMessage>
                          ))}
                        </>
                      )}

                      {generationResult.validationResult.suggestions?.length > 0 && (
                        <>
                          <ThemedText type="defaultSemiBold" style={{ marginBottom: theme.spacing.sm }}>
                            Suggestions
                          </ThemedText>
                          {generationResult.validationResult.suggestions.map((suggestion, index) => (
                            <ValidationMessage key={index} severity="Info" theme={theme}>
                              {suggestion}
                            </ValidationMessage>
                          ))}
                        </>
                      )}
                    </ValidationSection>
                  )}

                  {generationResult.reasoning && (
                    <FormGroup theme={theme}>
                      <ThemedText type="defaultSemiBold" style={{ marginBottom: theme.spacing.sm }}>
                        AI Reasoning
                      </ThemedText>
                      <ReasoningBox theme={theme}>
                        {generationResult.reasoning}
                      </ReasoningBox>
                    </FormGroup>
                  )}

                  {/* Chat refinement panel */}
                  <FormGroup theme={theme} style={{ borderTop: `1px solid ${theme.colors.border}`, paddingTop: theme.spacing.md, marginTop: theme.spacing.md }}>
                    <ThemedText type="defaultSemiBold" style={{ marginBottom: theme.spacing.sm }}>
                      Refine with AI
                    </ThemedText>
                    {conversationHistory.length > 0 && (
                      <div style={{ maxHeight: 150, overflowY: 'auto', marginBottom: theme.spacing.sm }}>
                        {conversationHistory.map((turn, i) => (
                          <div key={i} style={{
                            padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                            marginBottom: theme.spacing.xs,
                            borderRadius: theme.borderRadius.sm,
                            background: turn.role === 'user' ? `${theme.colors.tint}15` : theme.colors.backgroundSecondary,
                            borderLeft: `3px solid ${turn.role === 'user' ? theme.colors.tint : theme.colors.border}`,
                            fontSize: theme.typography.sizes.small,
                            color: theme.colors.text,
                          }}>
                            <strong>{turn.role === 'user' ? 'You' : 'AI'}:</strong> {turn.content}
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: theme.spacing.sm }}>
                      <TextArea
                        theme={theme}
                        value={chatMessage}
                        onChange={(e) => setChatMessage(e.target.value)}
                        placeholder='e.g. "Make the location field optional" or "Add a header before the photos"'
                        maxLength={1000}
                        style={{ minHeight: '60px', flex: 1 }}
                        onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey) handleRefine(); }}
                        disabled={isRefining}
                      />
                      <Button
                        variant="primary"
                        onClick={handleRefine}
                        disabled={isRefining || !chatMessage.trim()}
                        style={{ alignSelf: 'flex-end', whiteSpace: 'nowrap' }}
                      >
                        {isRefining ? '...' : <Sparkles size={14} style={{ verticalAlign: 'middle' }} />}
                      </Button>
                    </div>
                    <ThemedText style={{ fontSize: theme.typography.sizes.small, color: theme.colors.muted, marginTop: theme.spacing.xs }}>
                      Ctrl+Enter to send
                    </ThemedText>
                  </FormGroup>
                </>
              ) : (
                <ValidationMessage severity="Error" theme={theme}>
                  {generationResult.errorMessage || 'Failed to generate form'}
                </ValidationMessage>
              )}
            </ResultsContainer>
          )}
        </ModalBody>

        <ModalFooter theme={theme}>
          {!generationResult ? (
            <>
              <Button variant="secondary" onClick={handleClose} disabled={isGenerating}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleGenerate}
                disabled={isGenerating || !description.trim()}
              >
                {isGenerating ? 'Generating...' : <><Sparkles size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Generate Form</>}
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setGenerationResult(null)}>
                <ArrowLeft size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Back
              </Button>
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              {generationResult.success && (
                <Button variant="primary" onClick={handleApply}>
                  <Check size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Apply to Form Builder
                </Button>
              )}
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};
